import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DocumentsPanel from "../components/DocumentsPanel";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const EASE = [0.76, 0, 0.24, 1];

function Stat({ label, value, sub, testId }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="border hairline p-6 bg-card"
            data-testid={testId}
        >
            <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">{label}</div>
            <div className="mt-3 font-editorial text-5xl tracking-tighter leading-none">{value}</div>
            {sub && <div className="mt-2 text-xs font-mono-ui text-muted-foreground">{sub}</div>}
        </motion.div>
    );
}

function statusVariant(s) {
    switch (s) {
        case "Resolved":
            return "default";
        case "In Progress":
            return "secondary";
        case "Submitted":
            return "outline";
        default:
            return "outline";
    }
}

export default function Portal() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [disputes, setDisputes] = useState([]);
    const [history, setHistory] = useState([]);
    const [activity, setActivity] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [s, d, h, a] = await Promise.all([
                    api.get("/portal/summary"),
                    api.get("/portal/disputes"),
                    api.get("/portal/score-history"),
                    api.get("/portal/activity"),
                ]);
                setSummary(s.data);
                setDisputes(d.data);
                setHistory(h.data);
                setActivity(a.data);
            } catch (e) {
                /* handled by protected route */
            }
        })();
    }, []);

    if (!summary) {
        return (
            <div className="min-h-screen bg-background">
                <Nav />
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-32 text-muted-foreground font-mono-ui text-sm">
                    Loading your file…
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-28 md:pt-32 pb-24" data-testid="portal-root">
                {/* Header */}
                <div className="grid grid-cols-12 gap-6 items-end mb-10 md:mb-14">
                    <div className="col-span-12 md:col-span-8">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-4 flex items-center gap-3">
                            <span className="w-8 h-px bg-muted-foreground" />
                            Client Portal · File in progress
                        </div>
                        <h1 className="font-editorial text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                            Good to see you,
                            <br />
                            <span className="italic text-accent">{user?.name?.split(" ")[0] || "Client"}.</span>
                        </h1>
                    </div>
                    <div className="col-span-12 md:col-span-4 md:text-right">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                            Case ID
                        </div>
                        <div className="font-mono-ui text-sm">ACR-{user?.id?.slice(-6).toUpperCase()}</div>
                    </div>
                </div>

                {/* Enrollment CTA — only shows if user has never paid */}
                {!user?.enrolled && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                        className="mb-8 border hairline bg-accent text-accent-foreground p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
                        data-testid="portal-enroll-cta"
                    >
                        <div className="flex items-start gap-4">
                            <Sparkles size={22} strokeWidth={1.4} />
                            <div>
                                <div className="text-[10px] tracking-[0.24em] uppercase text-accent-foreground/70">
                                    Get Started
                                </div>
                                <div className="mt-1 font-editorial text-2xl md:text-3xl tracking-tight">
                                    Enroll for a full year of credit repair — <span className="italic">$500</span>.
                                </div>
                                <div className="mt-2 text-sm text-accent-foreground/85 max-w-lg">
                                    Includes monthly dispute filing, collections removal, identity theft
                                    resolution and the 93-point program guarantee.
                                </div>
                            </div>
                        </div>
                        <Link
                            to="/enroll"
                            data-testid="portal-enroll-btn"
                            className="group inline-flex items-center gap-3 bg-background text-foreground px-5 py-4 text-[12px] tracking-[0.18em] uppercase hover:bg-foreground hover:text-background whitespace-nowrap"
                        >
                            Get Started
                            <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </Link>
                    </motion.div>
                )}

                {/* Stat grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat
                        label="Current FICO"
                        value={summary.current_score}
                        sub={`Started at ${summary.starting_score}`}
                        testId="stat-current-score"
                    />
                    <Stat
                        label="Lift"
                        value={`+${summary.delta}`}
                        sub="Total point change"
                        testId="stat-delta"
                    />
                    <Stat
                        label="Resolved"
                        value={summary.resolved}
                        sub={`of ${summary.total_items} disputes`}
                        testId="stat-removed"
                    />
                    <Stat
                        label="Active Items"
                        value={summary.in_progress + summary.submitted}
                        sub="Under active challenge"
                        testId="stat-active"
                    />
                </div>

                {/* Chart + activity */}
                <div className="mt-6 grid grid-cols-12 gap-4">
                    <div className="col-span-12 lg:col-span-8 border hairline bg-card p-6 md:p-8" data-testid="score-chart">
                        <div className="flex items-baseline justify-between mb-6">
                            <div>
                                <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                                    Score trajectory
                                </div>
                                <div className="font-editorial text-3xl md:text-4xl tracking-tighter">
                                    8-month view
                                </div>
                            </div>
                            <div className="font-mono-ui text-xs text-muted-foreground">
                                FICO 8 · Experian
                            </div>
                        </div>
                        <div className="h-64 md:h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="1 4" stroke="hsl(var(--border))" vertical={false} />
                                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} axisLine={false} tickLine={false} domain={["dataMin - 20", "dataMax + 20"]} />
                                    <Tooltip
                                        contentStyle={{
                                            background: "hsl(var(--background))",
                                            border: "1px solid hsl(var(--border))",
                                            borderRadius: 0,
                                            fontFamily: "IBM Plex Mono",
                                            fontSize: 12,
                                        }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="score"
                                        stroke="hsl(var(--accent))"
                                        strokeWidth={2}
                                        dot={{ r: 3, fill: "hsl(var(--accent))" }}
                                        activeDot={{ r: 5 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-4 border hairline bg-card p-6 md:p-8" data-testid="activity-feed">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground mb-6">
                            Activity Feed
                        </div>
                        <ol className="space-y-6">
                            {activity.map((a, i) => (
                                <li key={a.id} className="grid grid-cols-12 gap-3 border-t hairline pt-4">
                                    <div className="col-span-2 font-mono-ui text-xs text-muted-foreground">
                                        {String(activity.length - i).padStart(2, "0")}
                                    </div>
                                    <div className="col-span-10">
                                        <div className="font-editorial text-lg leading-tight tracking-tight">
                                            {a.title}
                                        </div>
                                        <div className="mt-1 text-xs text-muted-foreground">{a.detail}</div>
                                        <div className="mt-2 font-mono-ui text-[10px] tracking-[0.16em] uppercase text-muted-foreground">
                                            {a.days_ago} days ago
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>

                {/* Disputes table with tabs */}
                <div className="mt-6 border hairline bg-card p-6 md:p-8" data-testid="disputes-panel">
                    <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
                        <div>
                            <div className="text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                                Disputes
                            </div>
                            <div className="font-editorial text-3xl md:text-4xl tracking-tighter">
                                Ledger
                            </div>
                        </div>
                        <a
                            href="tel:5127831895"
                            className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase border border-foreground px-4 py-3 hover:bg-foreground hover:text-background"
                            data-testid="portal-call-cta"
                        >
                            Talk to your specialist
                            <ArrowUpRight size={14} />
                        </a>
                    </div>

                    <Tabs defaultValue="all" className="w-full">
                        <TabsList className="rounded-none border hairline bg-transparent p-0 h-auto flex-wrap">
                            {["all", "Submitted", "In Progress", "Resolved"].map((t) => (
                                <TabsTrigger
                                    key={t}
                                    value={t}
                                    className="rounded-none px-4 py-2 text-[11px] tracking-[0.16em] uppercase data-[state=active]:bg-foreground data-[state=active]:text-background"
                                    data-testid={`tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    {t === "all" ? "All" : t}
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        {["all", "Submitted", "In Progress", "Resolved"].map((tab) => {
                            const rows = tab === "all" ? disputes : disputes.filter((d) => d.status === tab);
                            return (
                                <TabsContent key={tab} value={tab} className="mt-6">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Creditor</TableHead>
                                                <TableHead>Bureau</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead>Amount</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="min-w-[160px]">Progress</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {rows.map((d) => (
                                                <TableRow key={d.id} data-testid={`dispute-row-${d.id}`}>
                                                    <TableCell className="font-editorial text-lg">{d.creditor}</TableCell>
                                                    <TableCell className="font-mono-ui text-xs">{d.bureau}</TableCell>
                                                    <TableCell className="text-sm">{d.type}</TableCell>
                                                    <TableCell className="font-mono-ui text-xs">
                                                        {d.amount ? `$${d.amount.toLocaleString()}` : "—"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={statusVariant(d.status)} className="rounded-none uppercase tracking-[0.14em] text-[10px]">
                                                            {d.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Progress value={d.progress} className="h-1 rounded-none" />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {rows.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                                                        No items in this stage.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TabsContent>
                            );
                        })}
                    </Tabs>
                </div>

                {/* Documents */}
                <div className="mt-6">
                    <DocumentsPanel />
                </div>
            </div>
        </div>
    );
}
